import React, { useState, useMemo, useEffect } from "react";
import { IoIosArrowDropdown } from "react-icons/io";
import { RiResetLeftLine } from "react-icons/ri";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const NonWorkingController = ({
  data,
  setDataForTable,
  setFilteredData,
  openStates,
  setOpenStates,
}) => {
  // State for search inputs
  const [searchValues, setSearchValues] = useState({
    MACHINE_DATE: "",
    SECTION_NAME: "",
    MACHINE_NAME: "",
    CAUSE: "",
  });

  // State for selected filters
  const [selectedMonth, setSelectedMonth] = useState(new Set());
  const [selectedSection, setSelectedSection] = useState(new Set());
  const [selectedMachine, setSelectedMachine] = useState(new Set());
  const [selectedCause, setSelectedCause] = useState(new Set());

  // Update table data based on selected filters
  useEffect(() => {
    setDataForTable({
      month: Array.from(selectedMonth),
      section: Array.from(selectedSection),
      machine: Array.from(selectedMachine),
      cause: Array.from(selectedCause),
    });
  }, [
    selectedMonth,
    selectedSection,
    selectedMachine,
    selectedCause,
    setDataForTable,
  ]);

  // Toggle selection for checkboxes
  const toggleSelection = (setFunc, currentSet, value) => {
    const newSet = new Set(currentSet);
    newSet.has(value) ? newSet.delete(value) : newSet.add(value);
    setFunc(newSet);
  };

  // Toggle dropdown visibility
  const toggleDropdown = (title) => {
    setOpenStates((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  // Handle search input changes
  const handleSearchChange = (title, value) => {
    setSearchValues((prev) => ({
      ...prev,
      [title]: value,
    }));
  };

  // Initialize filter options
  const defaultSetup = () => {
    if (!data?.length) return;
    const allMonths = [
      ...new Set(
        data.map((d) => d.MACHINE_DATE?.slice(0, 7)).filter((v) => v != null)
      ),
    ];
    const allSections = [
      ...new Set(data.map((d) => d.SECTION_NAME).filter((v) => v != null)),
    ];
    const allMachines = [
      ...new Set(data.map((d) => d.MACHINE_NAME).filter((v) => v != null)),
    ];
    const allCauses = [
      ...new Set(data.map((d) => d.CAUSE).filter((v) => v != null)),
    ];

    setSelectedMonth(new Set(allMonths));
    setSelectedSection(new Set(allSections));
    setSelectedMachine(new Set(allMachines));
    setSelectedCause(new Set(allCauses));
    setSearchValues({
      MACHINE_DATE: "",
      SECTION_NAME: "",
      MACHINE_NAME: "",
      CAUSE: "",
    });
    setOpenStates({
      MACHINE_DATE: false,
      SECTION_NAME: false,
      MACHINE_NAME: false,
      CAUSE: false,
    });
  };

  useEffect(() => {
    defaultSetup();
  }, [data]);

  // Available options for each filter
  const availableMonths = useMemo(() => {
    return [
      ...new Set(
        data.map((d) => d.MACHINE_DATE?.slice(0, 7)).filter((v) => v != null)
      ),
    ];
  }, [data]);

  const availableSections = useMemo(() => {
    return [
      ...new Set(
        data
          ?.filter((entry) =>
            selectedMonth.has(entry.MACHINE_DATE?.slice(0, 7))
          )
          .map((d) => d.SECTION_NAME)
          .filter((v) => v != null)
      ),
    ];
  }, [data, selectedMonth]);

  const availableMachines = useMemo(() => {
    return [
      ...new Set(
        data
          ?.filter(
            (entry) =>
              selectedMonth.has(entry.MACHINE_DATE?.slice(0, 7)) &&
              selectedSection.has(entry.SECTION_NAME)
          )
          .map((d) => d.MACHINE_NAME)
          .filter((v) => v != null)
      ),
    ];
  }, [data, selectedMonth, selectedSection]);

  const availableCauses = useMemo(() => {
    return [
      ...new Set(
        data
          ?.filter(
            (entry) =>
              selectedMonth.has(entry.MACHINE_DATE?.slice(0, 7)) &&
              selectedSection.has(entry.SECTION_NAME) &&
              selectedMachine.has(entry.MACHINE_NAME)
          )
          .map((d) => d.CAUSE)
          .filter((v) => v != null)
      ),
    ];
  }, [data, selectedMonth, selectedSection, selectedMachine]);

  // Filtered data based on selections
  const filteredData = useMemo(() => {
    if (
      !data ||
      !Array.isArray(data) ||
      selectedMonth.size === 0 ||
      selectedSection.size === 0 ||
      selectedMachine.size === 0 ||
      selectedCause.size === 0
    ) {
      return [];
    }

    return data.filter((entry) => {
      return (
        selectedMonth.has(entry.MACHINE_DATE?.slice(0, 7)) &&
        selectedSection.has(entry.SECTION_NAME) &&
        selectedMachine.has(entry.MACHINE_NAME) &&
        selectedCause.has(entry.CAUSE)
      );
    });
  }, [data, selectedMonth, selectedSection, selectedMachine, selectedCause]);

  // Propagate filteredData to parent component
  useEffect(() => {
    setFilteredData(filteredData);
  }, [filteredData, setFilteredData]);

  // Render checkbox list for each filter
  const renderCheckboxList = (title, items, selectedSet, setFunc) => {
    const filteredItems = items.filter((item) =>
      item
        ?.toString()
        ?.toLowerCase()
        ?.includes(searchValues[title]?.toLowerCase() || "")
    );

    return (
      <div className="col-md mt-2 p-1">
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-light"
          style={{
            position: "relative",
            fontSize: 12,
            borderRadius: "5px",
          }}
        >
          <div
            className="papaDiv"
            onClick={() => toggleDropdown(title)}
            style={{
              border: "1px solid #ccc",
              padding: "8px",
              cursor: "pointer",
              minWidth: "150px",
              maxHeight: "50px",
              overflow: "hidden",
              userSelect: "none",
            }}
          >
            Select {title} {"  "}
            <IoIosArrowDropdown
              style={{
                fontSize: 15,
                transition: "transform 0.3s ease",
                transform: openStates[title]
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
              }}
            />
          </div>
          {openStates[title] && (
            <div
              className="papaDiv"
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                border: "1px solid #ccc",
                background: "#fff",
                zIndex: 10,
              }}
            >
              <strong>{title}</strong>
              <input
                style={{ height: "25px", width: "90%" }}
                className="form-control ms-2 mb-1 me-2"
                type="search"
                placeholder={`Search ${title}...`}
                aria-label={`Search ${title}`}
                value={searchValues[title] || ""}
                onChange={(e) => handleSearchChange(title, e.target.value)}
              />
              <hr className="m-0 p-0" />
              <div
                className="row suggestion mt-1"
                key={filteredItems.length}
                style={{
                  maxHeight: "250px",
                  minHeight: "50px",
                  overflowY: "auto",
                }}
              >
                {filteredItems.length > 0 ? (
                  filteredItems.map((item, index) => (
                    <div
                      key={index}
                      className="col-sx fade-in"
                      style={{ fontSize: "12px" }}
                    >
                      <label
                        className="ms-1 text-start"
                        key={item}
                        style={{ display: "block", cursor: "pointer" }}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedSet.has(item)}
                          onChange={() =>
                            toggleSelection(setFunc, selectedSet, item)
                          }
                        />{" "}
                        {item || "N/A"}
                      </label>
                    </div>
                  ))
                ) : (
                  <div className="col-md-12 text-center">No items found</div>
                )}
              </div>
              <hr className="m-0 p-0" />
              <div className="d-flex justify-content-evenly mt-1">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setFunc(new Set(filteredItems))}
                >
                  Select All
                </button>
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={() => setFunc(new Set())}
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="papaDiv">
      <div
        className="row"
        style={{
          border: "1px solid #ccc",
          background: "rgba(255, 255, 255, 0.22)",
          boxShadow: "5px 5px 20px rgba(7, 7, 7, 0.3)",
          backdropFilter: "blur(15px)",
          WebkitBackdropFilter: "blur(10px)",
        }}
      >
        {renderCheckboxList(
          "MACHINE_DATE",
          availableMonths,
          selectedMonth,
          setSelectedMonth
        )}
        {renderCheckboxList(
          "SECTION_NAME",
          availableSections,
          selectedSection,
          setSelectedSection
        )}
        {renderCheckboxList(
          "MACHINE_NAME",
          availableMachines,
          selectedMachine,
          setSelectedMachine
        )}
        {renderCheckboxList(
          "CAUSE",
          availableCauses,
          selectedCause,
          setSelectedCause
        )}
        <OverlayTrigger
          delay={{ show: 100, hide: 200 }}
          overlay={<Tooltip id="tooltip-reset">Reset Filter</Tooltip>}
        >
          <div
            className="btn btn-sm btn-danger d-flex justify-content-center align-items-center mt-1 mb-1"
            style={{
              maxWidth: "35px",
              boxShadow: "3px 3px 10px rgba(52, 80, 142, 0.5)",
            }}
            onClick={defaultSetup}
          >
            <RiResetLeftLine style={{ fontSize: "15px", fontWeight: "bold" }} />
          </div>
        </OverlayTrigger>
      </div>
    </div>
  );
};

export default NonWorkingController;
